import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	createRoute,
	createRouter,
	Outlet,
	type RouterHistory,
	redirect,
} from "@tanstack/react-router";
import { meQuery } from "./auth/queries";
import { safeRedirect } from "./auth/redirect";
import Catalogue from "./pages/Catalogue.tsx";
import Landing from "./pages/Landing.tsx";
import Login from "./pages/Login.tsx";
import Product from "./pages/Product.tsx";
import VerifyLogin from "./pages/VerifyLogin.tsx";

export interface RouterContext {
	queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
	component: () => <Outlet />,
});

/** `?q=` of the catalogue. The router parses numbers ("?q=20574587"): keep them as text. */
function validateCatalogSearch(search: Record<string, unknown>): {
	q?: string;
} {
	const { q } = search;
	if (typeof q === "number") return { q: String(q) };
	return typeof q === "string" && q ? { q } : {};
}

/** A text search parameter, dropped when absent or empty. */
const text = (value: unknown): string | undefined =>
	typeof value === "string" && value ? value : undefined;

const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	component: Landing,
});

/** `?redirect=` the page to open once signed in, `?error=` why a provider sign-in failed. */
const loginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/connexion",
	component: Login,
	validateSearch: (
		search: Record<string, unknown>,
	): { redirect?: string; error?: string } => ({
		redirect: text(search.redirect),
		error: text(search.error),
	}),
	// already signed in: straight to the page asked for
	beforeLoad: async ({ context, search }) => {
		const me = await context.queryClient.ensureQueryData(meQuery());
		if (me) throw redirect({ href: safeRedirect(search.redirect) });
	},
});

/** The page a magic link opens (`?token=`); it posts the token on a click, see VerifyLogin. */
const verifyRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/connexion/verifier",
	component: VerifyLogin,
	validateSearch: (search: Record<string, unknown>): { token?: string } => ({
		token: text(search.token),
	}),
});

/**
 * The pages that need an account: the catalogue and the guides. Without a session the browser
 * goes to the login page, which brings it back here afterwards.
 *
 * No route here prefetches a product (no `loader`): with `defaultPreload: "intent"` hovering a
 * link would open the guide, and opening guides is rate-limited.
 */
const signedInRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_auth",
	beforeLoad: async ({ context, location }) => {
		const me = await context.queryClient.ensureQueryData(meQuery());
		if (!me)
			throw redirect({ to: "/connexion", search: { redirect: location.href } });
	},
});

const catalogueRoute = createRoute({
	getParentRoute: () => signedInRoute,
	path: "/catalogue",
	component: Catalogue,
	validateSearch: validateCatalogSearch,
});

const productRoute = createRoute({
	getParentRoute: () => signedInRoute,
	path: "/products/$slug",
	component: function ProductRoute() {
		const { slug } = productRoute.useParams();
		return <Product slug={slug} />;
	},
});

const routeTree = rootRoute.addChildren([
	indexRoute,
	loginRoute,
	verifyRoute,
	signedInRoute.addChildren([catalogueRoute, productRoute]),
]);

export function createAppRouter({
	queryClient,
	history,
}: RouterContext & { history?: RouterHistory }) {
	return createRouter({
		routeTree,
		context: { queryClient },
		history,
		defaultPreload: "intent",
		scrollRestoration: true,
		defaultStructuralSharing: true,
		defaultPreloadStaleTime: 0,
	});
}

declare module "@tanstack/react-router" {
	interface Register {
		router: ReturnType<typeof createAppRouter>;
	}
}
